import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { debugLog, debugWarn, debugError } from '@/lib/debugLogger';
import { isDemoUser } from '@/lib/demoGuard';
import {
  claimRefreshLeadership,
  announceRefreshDone,
  wasSessionRecentlyUpdatedByPeer,
  destroySessionCoordinator,
} from '@/lib/sessionCoordinator';
import { setDemoClusterContext } from '@/config/demoMode';
export type AppRole = 'admin' | 'coadmin' | 'gestor' | 'profissional' | 'observador' | 'executor' | 'admin_cluster' | 'gestor_planejamento' | 'gestor_qualidade' | 'gestor_documentacao' | 'apontador' | 'gestor_execucao' | 'instituicao_financeira';

// ── Permission maps for granular module access ──────────────────────
// Defines which roles can EDIT each module (deny-by-default for unlisted modules)
export const MODULE_EDIT_PERMISSIONS: Record<string, AppRole[]> = {
  'execucao':       ['admin', 'coadmin', 'apontador'],
  'quality-gate':   ['admin', 'coadmin', 'gestor_qualidade'],
  'planning-gate':  ['admin', 'coadmin', 'gestor_planejamento'],
  'medicoes':       ['admin', 'coadmin', 'gestor_planejamento'],
  'contratos':      ['admin', 'coadmin', 'gestor_planejamento'],
  'arquivo':        ['admin', 'coadmin', 'gestor_documentacao'],
  'alertas':        ['admin', 'coadmin'],
  'usuarios':       ['admin', 'coadmin', 'admin_cluster'],
  'relatorios':     ['admin', 'coadmin'],
  'configuracoes':  ['admin', 'coadmin'],
  'clusters':       ['admin', 'coadmin'],
  'integracao':     ['admin', 'coadmin'],
  'sandbox':        ['admin'],
  'risk-intelligence': ['admin', 'coadmin', 'admin_cluster', 'gestor_execucao'],
};

// Defines which roles can VIEW (access) each route
export const MODULE_VIEW_PERMISSIONS: Record<string, AppRole[]> = {
  'mapa':           ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor','gestor_execucao'],
  '':               ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','observador','gestor','executor','gestor_execucao'],
  'contratos':      ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor','gestor_execucao'],
  'execucao':       ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor','gestor_execucao'],
  // VIEW restrita por etapa: apontador não vê gates; gestor_qualidade não vê planejamento.
  // gestor_execucao e gestor_planejamento podem visualizar (read-only) etapas anteriores/atual.
  'quality-gate':   ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','gestor','gestor_execucao'],
  'planning-gate':  ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_documentacao','gestor','gestor_execucao'],
  'medicoes':       ['admin','coadmin','admin_cluster','gestor_planejamento','gestor','gestor_execucao'],
  'arquivo':        ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor'],
  'manual':         ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor','observador','gestor_execucao'],
  'portfolio':      ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor','gestor_execucao','observador'],
  'exploracao':     ['admin','coadmin','admin_cluster'],
  'indicadores':    ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor','gestor_execucao'],
  'alertas':        ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor','gestor_execucao'],
  'relatorios':     ['admin','coadmin','admin_cluster','gestor_planejamento','gestor','gestor_execucao'],
  'auditoria':      ['admin','coadmin','admin_cluster'],
  'sla-uptime':     ['admin','coadmin','admin_cluster'],
  'usuarios':       ['admin','coadmin','admin_cluster'],
  'configuracoes':  ['admin','coadmin'],
  'clusters':       ['admin','coadmin'],
  'integracao':     ['admin','coadmin'],
  'bim':            ['admin','coadmin','admin_cluster'],
  'sandbox':        ['admin'],
  'gestor-performance': ['admin','coadmin','admin_cluster','gestor'],
  'risk-intelligence': ['admin','coadmin','admin_cluster','gestor_execucao'],
  'inteligencia':     ['admin','coadmin','admin_cluster','gestor_execucao','gestor_planejamento','gestor_qualidade','gestor'],
  'financiamento':    ['instituicao_financeira'],
};

interface GestorHub {
  hub_id: string;
  hub_name: string;
  cluster_id: string;
  cluster_name: string;
}

// Flag para indicar se o gestor não tem hubs (para UX)
// Quando true, exibir mensagem na agenda

interface ProfessionalInfo {
  professional_id: string;
  professional_name: string;
  hub_id: string;
  hub_name: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isCoAdmin: boolean;
  isGestor: boolean;
  isProfissional: boolean;
  isObservador: boolean;
  isExecutor: boolean;
  hasAccess: (requiredRoles: AppRole[]) => boolean;
  canEdit: boolean; // Legacy: True if user can create/edit/delete data (fallback)
  canEditModule: (moduleId: string) => boolean; // Granular: checks edit permission per module
  isManagerial: boolean; // True if user is admin, coadmin, or gestor (cannot do field inspections)
  gestorHubs: GestorHub[];
  gestorHubIds: string[];
  gestorClusterIds: string[];
  // Professional-specific info for mobile inspection flow
  professionalInfo: ProfessionalInfo | null;
  /** Demo/investor-presentation flag. Sourced from profiles.is_demo. Default false. */
  isDemo: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type BootstrapSource = 'listener' | 'getSession' | 'safety-net' | 'recovery';

type BootstrapRequest = {
  session: Session | null;
  event: AuthChangeEvent | 'INITIAL_SESSION';
  source: BootstrapSource;
};

// Global bootstrap lock (prevents concurrent bootstrap executions)
let authBootstrapInProgress = false;
let activeAuthStateSubscription: { unsubscribe: () => void } | null = null;

const PREVIEW_SESSION_STORAGE_KEYS = ['__lovable_token', 'netuno-preview-token'];

function clearSessionStoragePreservingPreviewKeys() {
  if (typeof window === 'undefined') return;

  try {
    const preservedEntries = PREVIEW_SESSION_STORAGE_KEYS
      .map((key) => [key, sessionStorage.getItem(key)] as const)
      .filter((entry): entry is readonly [string, string] => typeof entry[1] === 'string');

    sessionStorage.clear();

    preservedEntries.forEach(([key, value]) => {
      sessionStorage.setItem(key, value);
    });
  } catch {
    // no-op
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [gestorHubs, setGestorHubs] = useState<GestorHub[]>([]);
  const [professionalInfo, setProfessionalInfo] = useState<ProfessionalInfo | null>(null);
  const [isDemo, setIsDemo] = useState<boolean>(false);

  const fetchUserRole = useCallback(async (userId: string): Promise<{ role: AppRole | null; hadError: boolean }> => {
    try {
      debugLog('auth', 'Bootstrap::get_user_role.rpc:start', { userId });

      // RPC get_user_role now auto-provisions profile+role if missing (SECURITY DEFINER)
      const { data: rpcRole, error: rpcError } = await supabase
        .rpc('get_user_role', { _user_id: userId });

      if (!rpcError && rpcRole) {
        debugLog('auth', 'Bootstrap::get_user_role.rpc:ok', { userId, role: rpcRole });
        return { role: rpcRole as AppRole, hadError: false };
      }

      if (rpcError) {
        debugWarn('auth', 'Bootstrap::get_user_role.rpc:error — tentando fallback', {
          userId,
          message: rpcError.message,
          code: (rpcError as any)?.code,
        });
      } else {
        debugWarn('auth', 'Bootstrap::get_user_role.rpc:empty — tentando fallback', { userId });
      }

      // Fallback to direct table read if RPC is unavailable
      debugLog('auth', 'Bootstrap::user_roles.select:start', { userId });
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .limit(1)
        .maybeSingle();

      if (error) {
        debugError('auth', 'Bootstrap::user_roles.select:error', {
          userId,
          message: error.message,
          code: (error as any)?.code,
          rpcMessage: rpcError?.message,
        });
        return { role: null, hadError: true };
      }

      const fallbackRole = (data?.role as AppRole | null) ?? null;
      debugLog('auth', 'Bootstrap::user_roles.select:ok', { userId, role: fallbackRole });
      return { role: fallbackRole, hadError: false };
    } catch (err) {
      debugError('auth', 'Bootstrap::get_user_role:exception', {
        userId,
        message: err instanceof Error ? err.message : String(err),
      });
      return { role: null, hadError: true };
    }
  }, []);

  const fetchUserRoleWithRetry = useCallback(async (userId: string): Promise<{ role: AppRole | null; hadError: boolean }> => {
    const retryDelays = [0, 250, 750];
    let hadError = false;

    for (const delayMs of retryDelays) {
      if (delayMs > 0) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));
      }

      const result = await fetchUserRole(userId);
      if (!result.hadError) {
        return result;
      }

      hadError = true;
    }

    return { role: null, hadError };
  }, [fetchUserRole]);

  /**
   * Busca os hubs onde o usuário é gestor responsável.
   * 
   * REGRA DE GOVERNANÇA: Visibilidade automática via campo gerente_email do Hub.
   * Não depende mais de associação manual na tabela gestor_hubs.
   */
  const fetchGestorHubs = useCallback(async (userId: string) => {
    try {
      debugLog('auth', 'Bootstrap::cluster.get_user_email:start', { userId });

      // Buscar email do usuário via função RPC
      const { data: userEmail, error: emailError } = await supabase
        .rpc('get_user_email', { _user_id: userId });

      if (emailError || !userEmail) {
        debugWarn('auth', 'Bootstrap::cluster.get_user_email:error', {
          userId,
          message: emailError?.message ?? 'email vazio',
        });
        return [];
      }

      debugLog('auth', 'Bootstrap::cluster.get_user_email:ok', { userId, userEmail });

      // Buscar hubs onde o email do usuário corresponde ao gerente_email
      debugLog('auth', 'Bootstrap::cluster.hubs:start', { userId });
      const { data, error } = await supabase
        .from('hubs')
        .select(`
          id,
          name,
          cluster_id,
          clusters!inner (
            id,
            name
          )
        `)
        .ilike('gerente_email', userEmail)
        .eq('status', 'active');
      
      if (error) {
        debugWarn('auth', 'Bootstrap::cluster.hubs:error', {
          userId,
          message: error.message,
          code: (error as any)?.code,
        });
        return [];
      }

      const hubs = (data || []).map((hub: any) => ({
        hub_id: hub.id,
        hub_name: hub.name,
        cluster_id: hub.cluster_id,
        cluster_name: hub.clusters?.name || '',
      }));

      debugLog('auth', 'Bootstrap::cluster.hubs:ok', {
        userId,
        hubsCount: hubs.length,
        clusterIds: [...new Set(hubs.map(h => h.cluster_id))],
      });

      return hubs;
    } catch (err) {
      debugError('auth', 'Bootstrap::cluster.hubs:exception', {
        userId,
        message: err instanceof Error ? err.message : String(err),
      });
      return [];
    }
  }, []);

  const fetchProfessionalInfo = useCallback(async (userId: string): Promise<ProfessionalInfo | null> => {
    try {
      debugLog('auth', 'Bootstrap::professional.start', { userId });

      const { data, error } = await supabase
        .from('profiles')
        .select(`
          professional_id,
          professionals!inner (
            id,
            name,
            hub_id,
            hubs!inner (
              id,
              name
            )
          )
        `)
        .eq('id', userId)
        .maybeSingle();

      if (error || !data?.professionals) {
        debugWarn('auth', 'Bootstrap::professional.empty-or-error', {
          userId,
          hasRow: !!data,
          message: error?.message,
          code: (error as any)?.code,
        });
        return null;
      }

      const prof = data.professionals as any;
      const info = {
        professional_id: prof.id,
        professional_name: prof.name,
        hub_id: prof.hub_id,
        hub_name: prof.hubs?.name || '',
      };

      debugLog('auth', 'Bootstrap::professional.ok', {
        userId,
        professionalId: info.professional_id,
        hubId: info.hub_id,
      });

      return info;
    } catch (err) {
      debugError('auth', 'Bootstrap::professional.exception', {
        userId,
        message: err instanceof Error ? err.message : String(err),
      });
      return null;
    }
  }, []);

  const authSyncVersionRef = useRef(0);
  const resumeRefreshCooldownRef = useRef(0);
  const lastHiddenAtRef = useRef<number | null>(null);
  const roleRef = useRef<AppRole | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const loadingRef = useRef(true);
  const roleFetchInFlightRef = useRef<string | null>(null); // dedup guard
  const bootstrapRetryRef = useRef(0);
  const bootstrapRunIdRef = useRef(0);
  const pendingBootstrapRef = useRef<BootstrapRequest | null>(null);
  const hasBootstrapCompletedRef = useRef(false);
  const initialListenerBootstrapSeenRef = useRef(false);

  useEffect(() => {
    roleRef.current = role;
    currentUserIdRef.current = user?.id ?? null;
    loadingRef.current = loading;
  }, [role, user, loading]);

  const signOutSafely = useCallback(async (reason: string) => {
    const SIGNOUT_TIMEOUT_MS = 4_000;
    let timeoutHandle: number | null = null;

    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise<void>((resolve) => {
          timeoutHandle = window.setTimeout(resolve, SIGNOUT_TIMEOUT_MS);
        }),
      ]);
    } catch (err) {
      debugWarn('auth', 'signOut remoto falhou — prosseguindo com limpeza local', {
        reason,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
      }
    }
  }, []);

  /**
   * Emergency session cleanup — wipes all client-side auth state
   * when a corrupt/invalid session is detected.
   */
  const emergencyCleanup = useCallback(async () => {
    debugError('auth', 'Limpeza emergencial de sessão corrompida');
    await signOutSafely('emergency-cleanup');

    // Clear any stale auth storage while preserving preview token keys
    try {
      const keysToRemove = Object.keys(localStorage).filter((k) => k.startsWith('sb-'));
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      // no-op
    }
    clearSessionStoragePreservingPreviewKeys();

    roleRef.current = null;
    currentUserIdRef.current = null;
    setUser(null);
    setSession(null);
    setRole(null);
    setGestorHubs([]);
    setProfessionalInfo(null);
    setLoading(false);
  }, [signOutSafely]);

  const withTimeout = useCallback(async <T,>(
    promise: Promise<T>,
    timeoutMs: number,
    label: string,
    fallback: T
  ): Promise<T> => {
    let timeoutHandle: number | null = null;

    const timeoutPromise = new Promise<T>((resolve) => {
      timeoutHandle = window.setTimeout(() => {
        debugWarn('auth', `${label} excedeu timeout`, { timeoutMs });
        resolve(fallback);
      }, timeoutMs);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
      }
    }
  }, []);

  const applySessionState = useCallback(async (
    currentSession: Session | null,
    syncVersion: number,
    authEvent: AuthChangeEvent | 'INITIAL_SESSION',
    runId: number,
  ) => {
    if (syncVersion !== authSyncVersionRef.current) return;

    const incomingUserId = currentSession?.user?.id ?? null;
    const isSameUser = !!incomingUserId && incomingUserId === currentUserIdRef.current;
    const shouldSkipRoleHydration =
      (authEvent === 'TOKEN_REFRESHED' || authEvent === 'SIGNED_IN')
      && isSameUser && roleRef.current !== null;

    debugLog('auth', 'Bootstrap::session.received', {
      runId,
      event: authEvent,
      hasSession: !!currentSession,
      userId: incomingUserId,
      isSameUser,
      shouldSkipRoleHydration,
    });

    console.log('[NetUno:AUTH_EVENT]', {
      runId,
      event: authEvent,
      hasSession: !!currentSession,
      userId: incomingUserId,
      isSameUser,
      shouldSkipRoleHydration,
      route: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : 'server',
      at: new Date().toISOString(),
    });

    // Only block UI when identity/role needs rehydration
    if (currentSession?.user && !shouldSkipRoleHydration) {
      setLoading(true);
    }

    setSession(currentSession);
    setUser(currentSession?.user ?? null);

    if (!currentSession?.user) {
      debugLog('auth', 'Bootstrap::session.empty', { runId, event: authEvent });
      setRole(null);
      roleRef.current = null;
      currentUserIdRef.current = null;
      setGestorHubs([]);
      setProfessionalInfo(null);
      setLoading(false);
      return;
    }

    if (shouldSkipRoleHydration) {
      debugLog('auth', 'Bootstrap::token_refresh.skip_hydration', { runId, userId: incomingUserId });
      setLoading(false);
      return;
    }

    const userId = currentSession.user.id;

    // Stage 0: validate backend user state — CRITICAL for stale session detection
    debugLog('auth', 'Bootstrap::auth.getUser:start', { runId, userId });
    const { data: getUserData, error: getUserError } = await supabase.auth.getUser();
    if (getUserError) {
      const isFatalSessionError =
        getUserError.message?.includes('session_not_found') ||
        getUserError.message?.includes('Session from session_id') ||
        (getUserError as any)?.code === 'session_not_found' ||
        (getUserError as any)?.status === 403;

      debugWarn('auth', 'Bootstrap::auth.getUser:error', {
        runId,
        userId,
        message: getUserError.message,
        code: (getUserError as any)?.code,
        isFatal: isFatalSessionError,
      });

      if (isFatalSessionError) {
        debugError('auth', 'Bootstrap::session_not_found — forçando logout limpo', { runId, userId });

        // Clear stale auth tokens from storage preserving preview token keys
        try {
          const keysToRemove = Object.keys(localStorage).filter((k) => k.startsWith('sb-'));
          keysToRemove.forEach((k) => localStorage.removeItem(k));
        } catch {
          // no-op
        }
        clearSessionStoragePreservingPreviewKeys();

        await signOutSafely('fatal-session-not-found');

        // Reset all auth state to unauthenticated
        roleRef.current = null;
        currentUserIdRef.current = null;
        setUser(null);
        setSession(null);
        setRole(null);
        setGestorHubs([]);
        setProfessionalInfo(null);
        setLoading(false);
        return; // Exit bootstrap — ProtectedRoute will redirect to /auth
      }
    } else {
      debugLog('auth', 'Bootstrap::auth.getUser:ok', {
        runId,
        userId,
        returnedUserId: getUserData?.user?.id ?? null,
      });
    }

    // Stage 1: load profile (non-blocking for session validity)
    debugLog('auth', 'Bootstrap::profiles.select:start', { runId, userId });
    const { data: profileRow, error: profileError } = await (supabase as any)
      .from('profiles')
      .select('id, cluster_id, is_demo')
      .eq('id', userId)
      .maybeSingle();

    if (profileError) {
      debugWarn('auth', 'Bootstrap::profiles.select:error', {
        runId,
        userId,
        message: profileError.message,
        code: (profileError as any)?.code,
      });
      setIsDemo(false);
      setDemoClusterContext(null);
    } else if (!profileRow) {
      debugWarn('auth', 'Bootstrap::profiles.select:empty', { runId, userId });
      setIsDemo(false);
      setDemoClusterContext(null);
    } else {
      debugLog('auth', 'Bootstrap::profiles.select:ok', {
        runId,
        userId,
        clusterId: profileRow.cluster_id ?? null,
        isDemo: !!profileRow.is_demo,
      });
      const effectiveIsDemo = isDemoUser({ isDemo: profileRow.is_demo });
      setIsDemo(effectiveIsDemo);
      // Runtime-only demo cluster context: alinha visibilidade de contratos
      // demo ao cluster do usuário demo, evitando bloqueios silenciosos de RLS
      // no fluxo ponta-a-ponta. Nunca persistido, nunca enviado ao backend.
      setDemoClusterContext(effectiveIsDemo ? (profileRow.cluster_id ?? null) : null);
    }

    // Dedup: skip if another fetch for same user is already in-flight
    if (roleFetchInFlightRef.current === userId && roleRef.current !== null) {
      debugLog('auth', 'Bootstrap::get_user_role.dedup', { runId, userId, role: roleRef.current });
      setLoading(false);
      return;
    }
    roleFetchInFlightRef.current = userId;

    const { role: fetchedRole, hadError } = await withTimeout(
      fetchUserRoleWithRetry(userId),
      10_000,
      'fetchUserRoleWithRetry',
      { role: null, hadError: true },
    );

    roleFetchInFlightRef.current = null;

    if (syncVersion !== authSyncVersionRef.current) return;

    let effectiveRole = fetchedRole;
    if (hadError && isSameUser && roleRef.current) {
      effectiveRole = roleRef.current;
      debugWarn('auth', 'Bootstrap::get_user_role.error_keep_memory_role', {
        runId,
        userId,
        role: effectiveRole,
      });
    }

    if (!effectiveRole) {
      debugError('auth', 'Bootstrap::get_user_role.unavailable — encerrando sessão por segurança', {
        runId,
        userId,
        hadError,
      });
      await emergencyCleanup();
      return;
    }

    debugLog('auth', 'Bootstrap::permissions.role_resolved', { runId, userId, role: effectiveRole });

    setRole(effectiveRole);
    roleRef.current = effectiveRole;
    currentUserIdRef.current = userId;

    if (effectiveRole === 'gestor') {
      const hubs = await withTimeout(fetchGestorHubs(userId), 10_000, 'fetchGestorHubs', [] as GestorHub[]);
      if (syncVersion !== authSyncVersionRef.current) return;
      setGestorHubs(hubs);
      setProfessionalInfo(null);
      debugLog('auth', 'Bootstrap::cluster.loaded', { runId, userId, hubsCount: hubs.length });
    } else if (effectiveRole === 'coadmin') {
      setGestorHubs([]);
      setProfessionalInfo(null);
      debugLog('auth', 'Bootstrap::cluster.not_required', { runId, userId, role: effectiveRole });
    } else if (effectiveRole === 'profissional' || effectiveRole === 'executor') {
      const profInfo = await withTimeout(fetchProfessionalInfo(userId), 10_000, 'fetchProfessionalInfo', null as ProfessionalInfo | null);
      if (syncVersion !== authSyncVersionRef.current) return;
      setProfessionalInfo(profInfo);
      setGestorHubs([]);
      debugLog('auth', 'Bootstrap::professional.loaded', {
        runId,
        userId,
        hasProfessionalInfo: !!profInfo,
        professionalId: profInfo?.professional_id ?? null,
      });
    } else {
      setGestorHubs([]);
      setProfessionalInfo(null);
      debugLog('auth', 'Bootstrap::profile_specific_data.not_required', { runId, userId, role: effectiveRole });
    }

    if (syncVersion === authSyncVersionRef.current) {
      setLoading(false);
    }
  }, [fetchUserRoleWithRetry, fetchGestorHubs, fetchProfessionalInfo, withTimeout, emergencyCleanup, signOutSafely]);

  useEffect(() => {
    let isMounted = true;
    initialListenerBootstrapSeenRef.current = false;
    hasBootstrapCompletedRef.current = false;

    const runBootstrap = async (request: BootstrapRequest) => {
      if (!isMounted) return;

      if (authBootstrapInProgress) {
        pendingBootstrapRef.current = request;
        debugWarn('auth', 'Bootstrap duplicado bloqueado (lock ativo)', {
          event: request.event,
          source: request.source,
        });
        return;
      }

      authBootstrapInProgress = true;
      const runId = ++bootstrapRunIdRef.current;
      const startedAt = Date.now();

      debugLog('auth', 'Bootstrap iniciado', {
        runId,
        event: request.event,
        source: request.source,
        hasSession: !!request.session,
      });

      try {
        const syncVersion = ++authSyncVersionRef.current;

        let bootstrapTimeoutHandle: number | null = null;
        try {
          await Promise.race([
            applySessionState(request.session, syncVersion, request.event, runId),
            new Promise<never>((_, reject) => {
              bootstrapTimeoutHandle = window.setTimeout(() => reject(new Error('BOOTSTRAP_TIMEOUT')), 12_000);
            }),
          ]);
        } finally {
          if (bootstrapTimeoutHandle !== null) {
            window.clearTimeout(bootstrapTimeoutHandle);
          }
        }

        bootstrapRetryRef.current = 0;
        hasBootstrapCompletedRef.current = true;

        debugLog('auth', 'Bootstrap concluído', {
          runId,
          event: request.event,
          source: request.source,
          durationMs: Date.now() - startedAt,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        debugError('auth', 'Bootstrap falhou', {
          runId,
          event: request.event,
          source: request.source,
          message,
        });

        if (isMounted && bootstrapRetryRef.current < 1) {
          bootstrapRetryRef.current += 1;
          debugWarn('auth', 'Reiniciando bootstrap automaticamente', {
            runId,
            retry: bootstrapRetryRef.current,
          });

          const { data: { session: recoverySession } } = await supabase.auth.getSession();
          pendingBootstrapRef.current = {
            session: recoverySession ?? null,
            event: 'INITIAL_SESSION',
            source: 'recovery',
          };
        } else if (isMounted) {
          const hasAuthenticatedSession = !!request.session?.user;
          const fallbackRole = roleRef.current ?? null;

          if (hasAuthenticatedSession && !fallbackRole) {
            debugError('auth', 'Bootstrap degradado sem role válida — forçando logout seguro', {
              runId,
              hasAuthenticatedSession,
            });
            await emergencyCleanup();
            return;
          }

          debugWarn('auth', 'Bootstrap degradado — sessão preservada com role em memória', {
            runId,
            hasAuthenticatedSession,
            fallbackRole,
          });

          setSession(request.session ?? null);
          setUser(request.session?.user ?? null);
          setRole(fallbackRole);
          roleRef.current = fallbackRole;
          currentUserIdRef.current = request.session?.user?.id ?? null;
          setGestorHubs([]);
          setProfessionalInfo(null);
          setLoading(false);
        }
      } finally {
        authBootstrapInProgress = false;
      }

      if (!isMounted) return;

      const pending = pendingBootstrapRef.current;
      if (pending) {
        pendingBootstrapRef.current = null;
        void runBootstrap(pending);
      }
    };

    const requestBootstrap = (
      session: Session | null,
      event: AuthChangeEvent | 'INITIAL_SESSION',
      source: BootstrapSource
    ) => {
      void runBootstrap({ session, event, source });
    };

    // Set up listener first, then session bootstrap; lock serializes both sources.
    if (activeAuthStateSubscription) {
      debugWarn('auth', 'Listener authStateChange duplicado detectado — removendo assinatura anterior');
      try {
        activeAuthStateSubscription.unsubscribe();
      } catch {
        // no-op
      }
      activeAuthStateSubscription = null;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      if (event === 'INITIAL_SESSION') {
        initialListenerBootstrapSeenRef.current = true;
      }

      console.log('[NetUno:AUTH_LISTENER]', {
        event,
        hasSession: Boolean(currentSession),
        userId: currentSession?.user?.id ?? null,
        route: `${window.location.pathname}${window.location.search}`,
        at: new Date().toISOString(),
      });

      requestBootstrap(currentSession, event, 'listener');
    });

    activeAuthStateSubscription = subscription;

    void supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (!isMounted) return;

      if (initialListenerBootstrapSeenRef.current || hasBootstrapCompletedRef.current) {
        debugLog('auth', 'Bootstrap via getSession suprimido (listener já inicializou)', {
          hasBootstrapCompleted: hasBootstrapCompletedRef.current,
          listenerSeen: initialListenerBootstrapSeenRef.current,
        });
        return;
      }

      requestBootstrap(existingSession ?? null, 'INITIAL_SESSION', 'getSession');
    });

    // Safety-net: if still loading, restart deterministically instead of rendering prematurely
    const bootstrapSafetyTimer = window.setTimeout(() => {
      if (!isMounted || !loadingRef.current) return;

      debugWarn('auth', 'Safety-net de bootstrap acionado — reiniciando fluxo', {
        hasBootstrapCompleted: hasBootstrapCompletedRef.current,
      });

      void supabase.auth.getSession().then(({ data: { session: fallbackSession } }) => {
        if (!isMounted) return;
        requestBootstrap(fallbackSession ?? null, 'INITIAL_SESSION', 'safety-net');
      });
    }, 8_000);

    // ── Resilient refresh with retry + backoff ──────────────────────
    // Instead of immediately cleaning up on first failure, retry up to 3 times
    // with exponential backoff. Only perform emergency cleanup on confirmed
    // fatal errors after all retries are exhausted.
    const FATAL_ERROR_PATTERNS = [
      'Refresh Token Not Found',
      'Invalid Refresh Token',
      'invalid claim',
      'session_not_found',
      'User not found',
    ];

    const isTransientError = (error: { message?: string; status?: number; code?: string }): boolean => {
      const msg = error.message ?? '';
      // If it matches any fatal pattern, it's NOT transient
      if (FATAL_ERROR_PATTERNS.some((p) => msg.includes(p))) return false;
      // Network errors, timeouts, 5xx — all transient
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('fetch')) return true;
      if ((error as any)?.status >= 500) return true;
      if (msg.includes('timeout') || msg.includes('TIMEOUT')) return true;
      // Default: treat unknown errors as transient (conservative — don't logout)
      return true;
    };

    const refreshWithRetry = async (reason: string): Promise<boolean> => {
      // Multi-tab coordination: check if another tab is handling refresh
      if (!claimRefreshLeadership()) {
        debugLog('auth', 'Refresh deferido para outra aba (BroadcastChannel)', { reason });
        return false;
      }

      // Wait for network if offline
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        debugLog('auth', 'Refresh adiado — offline, aguardando evento online', { reason });
        return false;
      }

      const retryDelays = [0, 2_000, 5_000]; // immediate, 2s, 5s
      for (let attempt = 0; attempt < retryDelays.length; attempt++) {
        if (!isMounted) return false;

        if (retryDelays[attempt] > 0) {
          await new Promise<void>((r) => window.setTimeout(r, retryDelays[attempt]));
        }
        if (!isMounted) return false;

        // Re-check: maybe another tab refreshed while we were waiting
        if (wasSessionRecentlyUpdatedByPeer()) {
          debugLog('auth', 'Refresh cancelado — outra aba atualizou sessão durante backoff', { reason, attempt });
          return false;
        }

        debugLog('auth', `Refresh tentativa ${attempt + 1}/${retryDelays.length}`, { reason });

        const { error } = await supabase.auth.refreshSession();
        if (!error) {
          debugLog('auth', 'Refresh bem-sucedido', { attempt: attempt + 1, reason });
          announceRefreshDone();
          return true;
        }

        if (!isTransientError(error)) {
          debugError('auth', 'Token irrecuperável confirmado — limpeza emergencial', {
            attempt: attempt + 1,
            message: error.message,
            reason,
          });
          await emergencyCleanup();
          return false;
        }

        // Transient error — will retry
        debugWarn('auth', 'Refresh falhou (transiente) — NÃO limpando sessão', {
          attempt: attempt + 1,
          message: error.message,
          reason,
        });
      }

      // All retries exhausted but error was transient — do NOT logout,
      // keep current session in memory and hope next cycle succeeds.
      debugWarn('auth', 'Todas as tentativas de refresh falharam (transiente) — sessão mantida em memória', { reason });
      return false;
    };

    // ── Proactive refresh on visibility/focus/online resume ──────────
    // This is the PRIMARY mechanism for surviving 70-90min idle.
    // Browser timers (setInterval) are throttled/paused in background tabs,
    // so we MUST rely on event-driven triggers.
    const refreshFromIdle = (triggerSource: 'visibility' | 'focus' | 'online' = 'visibility') => {
      if (document.visibilityState !== 'visible' && triggerSource !== 'online') {
        debugLog('visibility', 'refreshFromIdle ignorado — aba não visível');
        return;
      }

      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        debugLog('visibility', 'refreshFromIdle ignorado — offline');
        return;
      }

      const now = Date.now();
      if (now - resumeRefreshCooldownRef.current < 8_000) {
        debugLog('auth', 'Refresh suprimido por cooldown', { elapsed: now - resumeRefreshCooldownRef.current, triggerSource });
        return;
      }
      resumeRefreshCooldownRef.current = now;

      void supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
        if (!isMounted) return;

        if (!currentSession) {
          debugLog('auth', 'Sem sessão ativa — refresh não necessário');
          return;
        }

        const hiddenForMs = lastHiddenAtRef.current ? now - lastHiddenAtRef.current : 0;
        const expiresAtMs = (currentSession.expires_at ?? 0) * 1000;
        const timeToExpiry = expiresAtMs - now;
        // Refresh if token expires within 15 min (covers 1h JWT with margin)
        const tokenExpiringSoon = expiresAtMs > 0 && timeToExpiry < 15 * 60 * 1000;
        // Refresh after ANY idle > 60s to be conservative for long sleeps
        const resumedAfterIdle = hiddenForMs > 60_000;
        // Token already expired — must refresh immediately
        const tokenExpired = expiresAtMs > 0 && timeToExpiry <= 0;

        if (!tokenExpiringSoon && !resumedAfterIdle && !tokenExpired) {
          debugLog('auth', 'Refresh não necessário', {
            hiddenForMs,
            tokenExpiringSoon,
            resumedAfterIdle,
            expiresInSec: Math.round(timeToExpiry / 1000),
            triggerSource,
          });
          return;
        }

        debugLog('auth', 'Proactive refresh iniciado', {
          triggerSource,
          idleSeconds: Math.round(hiddenForMs / 1000),
          tokenExpiringSoon,
          tokenExpired,
          expiresInSec: Math.round(timeToExpiry / 1000),
        });

        await refreshWithRetry(`idle-resume-${triggerSource}`);
      });
    };

    // ── Session keepalive — prevents premature expiration during inactivity ──
    // Every 4 minutes, if the tab is visible and user is authenticated,
    // proactively refresh the token to prevent silent expiration.
    // NOTE: This timer may be throttled in background tabs — that's OK because
    // visibilitychange/focus/online handlers are the primary recovery mechanism.
    const KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000; // 4 minutes
    const keepaliveTimer = window.setInterval(() => {
      if (!isMounted) return;
      if (document.visibilityState !== 'visible') return;
      if (!currentUserIdRef.current) return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;

      void supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
        if (!isMounted || !currentSession) return;

        const now = Date.now();
        const expiresAtMs = (currentSession.expires_at ?? 0) * 1000;
        const timeToExpiry = expiresAtMs - now;

        // Refresh if token will expire within next 10 minutes
        if (expiresAtMs > 0 && timeToExpiry < 10 * 60 * 1000) {
          debugLog('auth', 'Keepalive: token expirando em breve, renovando', {
            expiresInSeconds: Math.round(timeToExpiry / 1000),
          });
          await refreshWithRetry('keepalive');
        }
      });
    }, KEEPALIVE_INTERVAL_MS);

    // ── Event handlers ──────────────────────
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAtRef.current = Date.now();
        debugLog('visibility', 'Aba ocultada', { timestamp: lastHiddenAtRef.current });
        return;
      }

      const hiddenForMs = lastHiddenAtRef.current ? Date.now() - lastHiddenAtRef.current : 0;
      debugLog('visibility', 'Aba retornou ao foco', { hiddenForMs });
      refreshFromIdle('visibility');
    };

    const handleFocus = () => refreshFromIdle('focus');

    const handleOnline = () => {
      debugLog('auth', 'Rede reconectada — verificando sessão');
      // Small delay to let network stabilize
      window.setTimeout(() => {
        if (!isMounted) return;
        refreshFromIdle('online');
      }, 1_500);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);

    return () => {
      isMounted = false;
      authSyncVersionRef.current += 1;
      authBootstrapInProgress = false;
      pendingBootstrapRef.current = null;
      initialListenerBootstrapSeenRef.current = false;
      window.clearTimeout(bootstrapSafetyTimer);
      window.clearInterval(keepaliveTimer);
      if (activeAuthStateSubscription === subscription) {
        try {
          activeAuthStateSubscription.unsubscribe();
        } catch {
          // no-op
        }
        activeAuthStateSubscription = null;
      } else {
        subscription.unsubscribe();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      destroySessionCoordinator();
    };
  }, [applySessionState, emergencyCleanup]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      
      if (error) {
        return { error: new Error(error.message) };
      }
      
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name: name.trim(),
          },
        },
      });
      
      if (error) {
        return { error: new Error(error.message) };
      }
      
      // Profile e role são criados automaticamente pelo trigger handle_new_user
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  const signOut = async () => {
    await signOutSafely('user-signout');

    // Thorough storage cleanup to prevent stale session persistence
    try {
      const keysToRemove = Object.keys(localStorage).filter((k) => k.startsWith('sb-'));
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      // no-op
    }
    clearSessionStoragePreservingPreviewKeys();

    roleRef.current = null;
    currentUserIdRef.current = null;
    setUser(null);
    setSession(null);
    setRole(null);
    setGestorHubs([]);
    setProfessionalInfo(null);
    setIsDemo(false);
    setDemoClusterContext(null);
  };

  const isAdmin = role === 'admin';
  const isCoAdmin = role === 'coadmin';
  const isGestor = role === 'gestor';
  const isProfissional = role === 'profissional';
  const isObservador = role === 'observador';
  const isExecutor = role === 'executor';
  
  // Managerial roles cannot do field inspections (admin, coadmin, gestor)
  const isManagerial = role === 'admin' || role === 'coadmin' || role === 'gestor' || role === 'admin_cluster' || role === 'gestor_planejamento' || role === 'gestor_qualidade' || role === 'gestor_documentacao' || role === 'gestor_execucao';
  
  // Legacy: Observador cannot edit, all others can edit freely
  const canEdit = role !== null && role !== 'observador';

  // Granular: checks edit permission per module (deny-by-default)
  const canEditModule = useCallback((moduleId: string): boolean => {
    if (!role) return false;
    if (role === 'admin' || role === 'coadmin') return true;
    const allowed = MODULE_EDIT_PERMISSIONS[moduleId];
    if (!allowed) return false; // deny-by-default
    return allowed.includes(role);
  }, [role]);

  const hasAccess = useCallback((requiredRoles: AppRole[]) => {
    if (!role) return false;
    return requiredRoles.includes(role);
  }, [role]);

  // Derived values for gestor hub/cluster access
  const gestorHubIds = gestorHubs.map(h => h.hub_id);
  const gestorClusterIds = [...new Set(gestorHubs.map(h => h.cluster_id))];

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        role,
        loading,
        signIn,
        signUp,
        signOut,
        isAdmin,
        isCoAdmin,
        isGestor,
        isProfissional,
        isObservador,
        isExecutor,
        hasAccess,
        canEdit,
        canEditModule,
        isManagerial,
        gestorHubs,
        gestorHubIds,
        gestorClusterIds,
        professionalInfo,
        isDemo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
